// Pagamento em partes no Contas a Pagar (07/10/2026) — a régua da TELA.
//
// A régua que vale é a do servidor (dre-engine/handlers/pagamentosCP.js): ele
// confere de novo e recusa. Esta aqui só existe para a janela avisar enquanto a
// pessoa digita e para a lista mostrar "Pago em parte" sem perguntar ao servidor.
//
// Cada pagamento é uma entrada de `baixas_ca`: 'sistema' = registrado na tela;
// 'extrato' = transação do banco vinculada pela conciliação; sem origem = baixa
// que veio do Conta Azul.
import { ContaPagar, STATUS_CP } from './types';

export type Baixa = NonNullable<ContaPagar['baixas_ca']>[number];

export type ParteForm = {
  chave: string;               // só para a lista da tela
  conta_financeira_id: string;
  valor: number | null;
  data_pagamento: string;      // YYYY-MM-DD
  metodo_pagamento: string;
};

const FOLGA = 0.02;
const cent = (v: unknown) => Math.round((parseFloat(String(v ?? 0)) || 0) * 100);

export const ehParteDaTela = (b: Baixa | null | undefined) => !!b && b.origem === 'sistema';
export const ehDoExtrato = (b: Baixa | null | undefined) => !!b && b.origem === 'extrato';

export function valorDaBaixa(b: Baixa): number {
  return cent(b?.valor_composicao?.valor_liquido ?? b?.valor_composicao?.valor_bruto) / 100;
}

/** De onde veio o pagamento, em palavras. */
export function origemDoPagamento(b: Baixa): string {
  if (ehParteDaTela(b)) return 'Registrado aqui';
  if (ehDoExtrato(b) && (b.substitui || []).length) {
    const v = (b.substitui || []).reduce((t, p) => t + cent(valorDaBaixa(p)), 0) / 100;
    return `Extrato do banco (no lugar do registrado aqui: ${brl(v)})`;
  }
  if (ehDoExtrato(b)) return 'Extrato do banco';
  return 'Conta Azul';
}

/** O lançamento foi pago (ou começou a ser) pela janela de pagamentos? */
// (inclui a parte que o extrato do banco substituiu — ela fica guardada
// dentro da entrada do extrato, em `substitui`)
export const emPartes = (c: Pick<ContaPagar, 'baixas_ca'>) =>
  (c.baixas_ca || []).some(b => ehParteDaTela(b) || (ehDoExtrato(b) && (b.substitui || []).length > 0));

/** Quanto já saiu: a lista de pagamentos ou o extrato (valor_pago), o maior. */
export function pagoDoCP(c: ContaPagar): number {
  const lista = (c.baixas_ca || []).reduce((s, b) => s + cent(valorDaBaixa(b)), 0);
  const viaExtrato = Math.abs(cent(c.valor_pago));
  return Math.max(lista, viaExtrato) / 100;
}

/** Quanto falta pagar (0 se pago ou cancelado). */
export function faltaPagarCP(c: ContaPagar): number {
  if (c.status === 'pago' || c.status === 'cancelado') return 0;
  const total = Math.abs(cent(c.valor));
  return Math.max(0, total - cent(pagoDoCP(c))) / 100;
}

/** Pago em parte: ainda em aberto, mas com dinheiro já saído. */
export function pagoEmParte(c: ContaPagar): boolean {
  if (c.status === 'pago' || c.status === 'cancelado') return false;
  const pago = pagoDoCP(c);
  return pago > 0 && faltaPagarCP(c) > FOLGA;
}

/** A etiqueta da situação (a lista e o detalhe usam a mesma). */
export function situacaoCP(c: ContaPagar): { tagColor: string; label: string } {
  if (pagoEmParte(c)) return { tagColor: 'orange', label: 'Pago em parte' };
  return STATUS_CP[c.status] || STATUS_CP.pago;
}

export function somaPartes(partes: ParteForm[]): number {
  return partes.reduce((s, p) => s + cent(p.valor), 0) / 100;
}

/** O que impede registrar (ou null). Espelha validarPartes do servidor. */
export function problemaNasPartes(partes: ParteForm[], falta: number, hoje: string): string | null {
  if (!partes.length) return 'Informe ao menos um pagamento.';
  for (let k = 0; k < partes.length; k++) {
    const p = partes[k];
    const n = partes.length > 1 ? `Pagamento ${k + 1}: ` : '';
    if (!p.conta_financeira_id) return `${n}escolha a conta de onde o dinheiro saiu.`;
    if (!(cent(p.valor) > 0)) return `${n}informe o valor.`;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(p.data_pagamento || '')) return `${n}informe a data.`;
    if (p.data_pagamento > hoje) return `${n}a data é depois de hoje — registre quando o dinheiro sair.`;
  }
  const soma = cent(somaPartes(partes));
  if (soma > cent(falta) + cent(FOLGA)) {
    return `Os pagamentos somam ${brl(soma / 100)}, mas falta pagar ${brl(falta)}.`;
  }
  return null;
}

/** Quanto ainda vai faltar depois de registrar estas partes. */
export function faltaDepois(partes: ParteForm[], falta: number): number {
  return Math.max(0, cent(falta) - cent(somaPartes(partes))) / 100;
}

/** Valor sugerido para uma linha nova: o que ainda falta depois das outras. */
export function sugestaoProximaParte(partes: ParteForm[], falta: number): number | null {
  const resto = faltaDepois(partes, falta);
  return resto > 0 ? resto : null;
}

export function brl(v: number): string {
  return 'R$ ' + (Math.abs(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
